import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend
} from "recharts";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

// Styled components
const Wrapper = styled.div`
  width: 100%;
  padding: 20px;
`;

const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const Controls = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  gap: 8px;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const ZakupyChangesByRegion = ({ purchaseData = [], clientsData = [] }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [hiddenRegions, setHiddenRegions] = useState(new Set());

  const monthNames = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
  ];

  const regionNameMapping = {
    MAZOWIECKIE: "mazowieckie", ŚLĄSKIE: "śląskie", PODKARPACKIE: "podkarpackie",
    LUBUSKIE: "lubuskie", ZACHODNIOPOMORSKIE: "zachodniopomorskie",
    WIELKOPOLSKIE: "wielkopolskie", DOLNOŚLĄSKIE: "dolnośląskie", ŁÓDZKIE: "łódzkie",
    ŚWIĘTOKRZYSKIE: "świętokrzyskie", POMORSKIE: "pomorskie", LUBELSKIE: "lubelskie",
    OPOLSKIE: "opolskie", PODLASKIE: "podlaskie", "WARMIŃSKO-MAZURSKIE": "warmińsko-mazurskie",
    "KUJAWSKO-POMORSKIE": "kujawsko-pomorskie", MAŁOPOLSKIE: "małopolskie"
  };

  useEffect(() => {
    if (!purchaseData.length || !clientsData.length) return;
    const years = [...new Set(purchaseData.map(row => row.Rok.toString()))].sort();
    setAvailableYears(years);
    setSelectedYear(years.includes("2024") ? "2024" : years[years.length - 1]);
  }, [purchaseData, clientsData]);

  useEffect(() => {
    if (!selectedYear || !purchaseData.length || !clientsData.length) return;

    const nipToRegion = {};
    clientsData.forEach(c => {
      const nip = c.nip?.trim();
      const reg = c.wojewodztwo?.trim()?.toUpperCase();
      if (nip) {
        nipToRegion[nip] = regionNameMapping[reg] || "Unknown";
      }
    });

    const monthlyData = Array.from({ length: 12 }, (_, i) => ({ month: monthNames[i] }));

    purchaseData
      .filter(row => row.Rok.toString() === selectedYear)
      .forEach(row => {
        const nip = row.NrDostawcy?.trim();
        const region = nipToRegion[nip] || "Unknown";
        const monthIdx = parseInt(row.Miesiac, 10) - 1;

        const amount = parseFloat(row.K_42) || 0;

        if (!monthlyData[monthIdx][region]) {
          monthlyData[monthIdx][region] = 0;
        }
        if (amount > 0) {
          monthlyData[monthIdx][region] += amount;
        }
      });

    setFilteredData(monthlyData);
  }, [purchaseData, clientsData, selectedYear]);

  const toggleRegion = (region) => {
    setHiddenRegions(prev => {
      const next = new Set(prev);
      next.has(region) ? next.delete(region) : next.add(region);
      return next;
    });
  };

  return (
    <Wrapper>
      <ChartTitle>
        Zmiany zakupów w województwach w czasie
        <InfoIconWrapper data-tooltip-id="zakupy-changes-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>
      <InfoTooltip
        id="zakupy-changes-tooltip"
        place="left"
        content={
          <>
            <strong>Zmiany wartości zakupów</strong><br />
            Pokazuje wartości zakupów miesięcznie,<br />
            w podziale na województwa.
          </>
        }
      />

      <Controls>
        <label>Rok:</label>
        <Select value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
          {availableYears.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </Select>
      </Controls>

      <ResponsiveContainer width="100%" height={700}>
        <LineChart data={filteredData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis tickFormatter={val => val.toLocaleString("pl-PL")} />
          <Tooltip formatter={val => val.toLocaleString("pl-PL")} />
          <Legend onClick={(e) => toggleRegion(e.value)} wrapperStyle={{ cursor: "pointer" }} />
          {Object.values(regionNameMapping).map((region, index) => (
            <Line
              key={region}
              type="monotone"
              dataKey={region}
              stroke={`hsl(${index * 20}, 70%, 50%)`}
              strokeWidth={2}
              dot={{ r: 3 }}
              hide={hiddenRegions.has(region)}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Wrapper>
  );
};

export default ZakupyChangesByRegion;
